import DOMPurify from 'dompurify';

/**
 * Security utilities for handling user input and preventing XSS attacks
 */

/**
 * Sanitizes a string to prevent XSS attacks using DOMPurify.
 * It strips all HTML tags but preserves newlines by converting them to <br> tags.
 * @param {string} str - The string to sanitize
 * @returns {string} The sanitized string
 */
export const sanitizeInput = (str: string): string => {
  if (typeof str !== 'string') {
    return '';
  }

  // First, sanitize the string to remove any malicious HTML/script tags
  const sanitized = DOMPurify.sanitize(str, { USE_PROFILES: { html: false } });

  // Then, replace newlines with <br> for display purposes and trim whitespace
  return sanitized.replace(/\n/g, '<br>').trim();
};

/**
 * Sanitizes a message object or string
 * @param {MessageContent} message - The message to sanitize
 * @returns {MessageContent} The sanitized message
 */
export const sanitizeMessage = (message: string | { userFacingMessage: string; internalMessage: any }): typeof message => {
  if (typeof message === 'string') {
    return sanitizeInput(message);
  }
  
  // For message objects, sanitize both user-facing and internal messages
  return {
    userFacingMessage: sanitizeInput(message.userFacingMessage),
    internalMessage: typeof message.internalMessage === 'string' 
      ? sanitizeInput(message.internalMessage) 
      : message.internalMessage // Don't sanitize non-string internal messages as they might be objects
  };
};
